import React, { useState, useMemo } from 'react';
import { Plus, Calendar, Package, UserPlus, Trash2, CheckCircle, XCircle, Download, Filter } from 'lucide-react';
import Card, { CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useAppStore } from '../store';
import { format, addDays, isAfter, isBefore, parseISO } from 'date-fns';
import ExpressModal from '../components/modals/ExpressModal';
import EntityModal from '../components/modals/EntityModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import Select from '../components/ui/Select';
import { useAuth } from '../context/AuthContext';

const Express: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // State for column clear confirmation
  const [clearColumnConfirmOpen, setClearColumnConfirmOpen] = useState(false);
  const [columnToClear, setColumnToClear] = useState<string | null>(null);
  
  // Filters
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [salespersonFilter, setSalespersonFilter] = useState<string>('all');
  const [deliveryDateFilter, setDeliveryDateFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  
  const { express, expressItems, clients, addClient, deleteExpress, updateExpress } = useAppStore();
  const { isAdmin } = useAuth();

  // Apply filters to express orders
  const filteredExpress = useMemo(() => {
    return express.filter(order => {
      // Filter by client
      if (clientFilter !== 'all' && order.client_id !== clientFilter) {
        return false;
      }

      // Filter by salesperson
      if (salespersonFilter !== 'all' && order.salesperson !== salespersonFilter) {
        return false;
      }

      // Filter by type (liso/estampado)
      if (typeFilter !== 'all') {
        const isPrinted = typeFilter === 'printed';
        if (order.is_printed !== isPrinted) {
          return false;
        }
      }

      // Filter by delivery date
      if (deliveryDateFilter !== 'all') {
        const today = new Date();
        const deliveryDate = parseISO(order.delivery_date);
        
        switch (deliveryDateFilter) {
          case '2days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 2));
          case '7days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 7));
          case '15days':
            return isAfter(deliveryDate, today) && 
                   isBefore(deliveryDate, addDays(today, 15));
          default:
            return true;
        }
      }

      return true;
    });
  }, [express, clientFilter, salespersonFilter, deliveryDateFilter, typeFilter]);

  // Group orders by status
  const ordersByStatus = {
    pending: filteredExpress.filter(order => order.status === 'pending'),
    in_progress: filteredExpress.filter(order => order.status === 'in_progress'),
    finished: filteredExpress.filter(order => order.status === 'finished'),
    shipped: filteredExpress.filter(order => order.status === 'shipped'),
  };

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Cliente no encontrado';
  };

  const getOrderItems = (orderId: string) => {
    return expressItems.filter(item => item.express_id === orderId);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, orderId: string) => {
    e.dataTransfer.setData('orderId', orderId);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, newStatus: Express['status']) => {
    e.preventDefault();
    const orderId = e.dataTransfer.getData('orderId');
    
    try {
      await updateExpress(orderId, { status: newStatus });
    } catch (error: any) {
      console.error('Error updating order status:', error);
      setError(error.message || 'Error al actualizar el estado del pedido');
      setShowErrorModal(true);
    }
  };

  const handleDelete = async () => {
    if (!orderToDelete) return;
    try {
      await deleteExpress(orderToDelete);
      setDeleteConfirmOpen(false);
      setOrderToDelete(null);
    } catch (error: any) {
      console.error('Error deleting order:', error);
      setError(error.message || 'Error al eliminar el pedido');
      setShowErrorModal(true);
    }
  };

  const handleEditOrder = (orderId: string) => {
    setSelectedOrder(orderId);
    setIsModalOpen(true);
  };

  // Reset all filters
  const resetFilters = () => {
    setClientFilter('all');
    setSalespersonFilter('all');
    setDeliveryDateFilter('all');
    setTypeFilter('all');
  };

  // Function to render DTF status indicators
  const renderDtfStatus = (order: typeof express[0]) => {
    return (
      <div className="flex flex-col gap-1 mt-2">
        {order.dtf_printed && (
          <div className="flex items-center text-xs">
            <span className="mr-1">DTF enviado:</span>
            {order.dtf_printed === 'Sí' ? (
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-red-500" />
            )}
          </div>
        )}
        
        {order.dtf_picked_up && (
          <div className="flex items-center text-xs">
            <span className="mr-1">DTF retirado:</span>
            {order.dtf_picked_up === 'Sí' ? (
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-red-500" />
            )}
          </div>
        )}
      </div>
    );
  };

  // Handle clear column
  const handleClearColumn = (status: string) => {
    setColumnToClear(status);
    setClearColumnConfirmOpen(true);
  };

  // Confirm clear column
  const confirmClearColumn = async () => {
    if (!columnToClear) return;
    
    try {
      // Get all order IDs for the selected status
      const orderIds = filteredExpress
        .filter(order => order.status === columnToClear)
        .map(order => order.id);
      
      // Delete each order
      for (const id of orderIds) {
        await deleteExpress(id);
      }
      
      setClearColumnConfirmOpen(false);
      setColumnToClear(null);
    } catch (error: any) {
      console.error('Error clearing column:', error);
      setError(error.message || 'Error al eliminar los pedidos');
      setShowErrorModal(true);
    }
  };

  const renderOrder = (order: typeof express[0]) => {
    const items = getOrderItems(order.id);
    const totalQty = items.reduce((sum, itm) => sum + itm.quantity, 0);
    
    // Keep the card background white regardless of is_printed
    const cardBgClass = 'bg-white dark:bg-gray-800';
    
    return (
      <div
        key={order.id}
        draggable
        onDragStart={(e) => handleDragStart(e, order.id)}
        onClick={() => handleEditOrder(order.id)}
        className={`${cardBgClass} p-4 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow relative group`}
      >
        {/* Delete button */}
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent opening edit modal
            setOrderToDelete(order.id);
            setDeleteConfirmOpen(true);
          }}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="h-4 w-4" />
        </button>

        <h4 className="font-medium text-gray-900 dark:text-white">{order.name}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{getClientName(order.client_id)}</p>
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-2">
          <Calendar className="h-4 w-4 mr-1" />
          {format(new Date(order.delivery_date), 'dd/MM/yyyy')}
        </div>
        
        {/* Type indicator */}
        <div className="mt-2 text-xs">
          <span className={`px-2 py-1 rounded-full ${
            order.is_printed 
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' 
              : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
          }`}>
            {order.is_printed ? 'Estampado' : 'Liso'}
          </span>
        </div>
        
        {/* DTF Status */}
        {(order.dtf_printed || order.dtf_picked_up) && renderDtfStatus(order)}
        
        {/* Shipping Label */}
        {order.shipping_label && (
          <div className="flex items-center mt-2">
            <a
              href={order.shipping_label}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              <Download className="h-3 w-3 mr-1" />
              Etiqueta de envío
            </a>
          </div>
        )}
        
        {/* Items Summary */}
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
            <Package className="h-4 w-4 mr-1" />
            <span>{totalQty} unidades</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Express Orders</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={<UserPlus className="h-5 w-5" />}
            onClick={() => setIsClientModalOpen(true)}
          >
            Nuevo Cliente
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={() => {
              setSelectedOrder(null);
              setIsModalOpen(true);
            }}
          >
            Agregar Pedido
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <Select
                label="Cliente"
                value={clientFilter}
                onChange={setClientFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  ...clients.map(client => ({
                    value: client.id,
                    label: client.name
                  }))
                ]}
              />
            </div>
            
            <div className="w-40">
              <Select
                label="Vendedor"
                value={salespersonFilter}
                onChange={setSalespersonFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'Mateo', label: 'Mateo' },
                  { value: 'Male', label: 'Male' }
                ]}
              />
            </div>
            
            <div className="w-40">
              <Select
                label="Entrega"
                value={deliveryDateFilter}
                onChange={setDeliveryDateFilter}
                options={[
                  { value: 'all', label: 'Todas' },
                  { value: '2days', label: 'Próx. 2 días' },
                  { value: '7days', label: 'Próx. 7 días' },
                  { value: '15days', label: 'Próx. 15 días' }
                ]}
              />
            </div>
            
            <div className="w-40">
              <Select
                label="Tipo"
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'plain', label: 'Liso' },
                  { value: 'printed', label: 'Estampado' }
                ]}
              />
            </div>
            
            <Button
              variant="outline"
              icon={<Filter className="h-4 w-4" />}
              onClick={resetFilters}
            >
              Limpiar Filtros
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Pedidos Express" />
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Pending Column */}
            <div 
              className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'pending')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Pendiente <span className="font-normal text-sm">({ordersByStatus.pending.length})</span>
                </h3>
                {isAdmin && ordersByStatus.pending.length > 0 && (
                  <button
                    onClick={() => handleClearColumn('pending')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todos los pedidos pendientes"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {ordersByStatus.pending.map(renderOrder)}
                {ordersByStatus.pending.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay pedidos pendientes
                  </div>
                )}
              </div>
            </div>

            {/* In Progress Column */}
            <div 
              className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'in_progress')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  En Proceso <span className="font-normal text-sm">({ordersByStatus.in_progress.length})</span>
                </h3>
                {isAdmin && ordersByStatus.in_progress.length > 0 && (
                  <button
                    onClick={() => handleClearColumn('in_progress')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todos los pedidos en proceso"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {ordersByStatus.in_progress.map(renderOrder)}
                {ordersByStatus.in_progress.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay pedidos en proceso
                  </div>
                )}
              </div>
            </div>

            {/* Finished Column */}
            <div 
              className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'finished')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Terminado <span className="font-normal text-sm">({ordersByStatus.finished.length})</span>
                </h3>
                {isAdmin && ordersByStatus.finished.length > 0 && (
                  <button
                    onClick={() => handleClearColumn('finished')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todos los pedidos terminados"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {ordersByStatus.finished.map(renderOrder)}
                {ordersByStatus.finished.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay pedidos terminados
                  </div>
                )}
              </div>
            </div>

            {/* Shipped Column */}
            <div 
              className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 'shipped')}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  Despachado <span className="font-normal text-sm">({ordersByStatus.shipped.length})</span>
                </h3>
                {isAdmin && ordersByStatus.shipped.length > 0 && (
                  <button
                    onClick={() => handleClearColumn('shipped')}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                    title="Eliminar todos los pedidos despachados"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {ordersByStatus.shipped.map(renderOrder)}
                {ordersByStatus.shipped.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    No hay pedidos despachados
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

<ExpressModal
  key={selectedOrder || 'new'}      /* 👈 ESTA LÍNEA ES CLAVE */
  isOpen={isModalOpen}
  onClose={() => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  }}
  orderId={selectedOrder}
/>


      <EntityModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        entityType="client"
        onSave={async (client) => {
          try {
            await addClient(client);
            setIsClientModalOpen(false);
          } catch (error: any) {
            console.error('Error creating client:', error);
            setError(error.message || 'Error al crear el cliente');
            setShowErrorModal(true);
          }
        }}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setOrderToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Eliminar Pedido"
        message={
          <div>
            <p>¿Estás seguro de que deseas eliminar este pedido? Esta acción no se puede deshacer.</p>
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-amber-800 dark:text-amber-200">⚠️ Eliminar este pedido no devuelve los ítems al stock. Solamente si corresponde, deberás reponerlos manualmente en la sección de stock.</p>
            </div>
          </div>
        }
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmModal
        isOpen={clearColumnConfirmOpen}
        onClose={() => {
          setClearColumnConfirmOpen(false);
          setColumnToClear(null);
        }}
        onConfirm={confirmClearColumn}
        title={`Eliminar todos los pedidos ${
          columnToClear === 'pending' ? 'pendientes' :
          columnToClear === 'in_progress' ? 'en proceso' :
          columnToClear === 'finished' ? 'terminados' : 'despachados'
        }`}
        message={
          <div>
            <p>{`¿Estás seguro de que deseas eliminar todos los pedidos ${
              columnToClear === 'pending' ? 'pendientes' :
              columnToClear === 'in_progress' ? 'en proceso' :
              columnToClear === 'finished' ? 'terminados' : 'despachados'
            }? Esta acción no se puede deshacer.`}</p>
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-amber-800 dark:text-amber-200">⚠️ Eliminar estos pedidos no devuelve los ítems al stock. Solamente si corresponde, deberás reponerlos manualmente en la sección de stock.</p>
            </div>
          </div>
        }
        confirmText="Eliminar todos"
        variant="danger"
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </div>
  );
};

export default Express;